import { CircularProgress } from "@mui/material";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import { TaskModel, TaskStatus } from "../../Models/Models";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { objectToFormData } from "../../Utils/logoUploadHelper";
import { componentNames } from "../../Utils/Constants";
import { urlEnum } from "../../Utils/urlEnum";
import { buildUrl, toISODate } from "../../Utils/tasksUtils";
import { TASKS_MAX_PER_PAGE } from "../../Utils/tasksConstants";
import styles from "../../Styles/calendarDashboard.module.css";
import TaskForm from "../Tasks/TaskForm";
import TaskDocGateDialog from "../Tasks/TaskDocGateDialog";
import CalendarHeader, { CalendarView } from "./CalendarHeader";
import CalendarLegend from "./CalendarLegend";
import CalendarMonthView from "./CalendarMonthView";
import CalendarWeekView from "./CalendarWeekView";

moment.locale("ro");

type State = {
  tasks: TaskModel[];
  loading: boolean;
  formOpen: boolean;
  selectedTask: TaskModel | null;
};

type DocGateState = {
  open: boolean;
  task: TaskModel | null;
  pendingStatus: TaskStatus | null;
};

function getViewRange(date: Moment, view: CalendarView): { startDate: string; endDate: string } {
  if (view === "month") {
    const start = date.clone().startOf("month").startOf("isoWeek");
    const end = date.clone().endOf("month").endOf("isoWeek");
    return { startDate: toISODate(start.toDate()), endDate: toISODate(end.toDate()) };
  }
  const start = date.clone().startOf("isoWeek");
  const end = date.clone().endOf("isoWeek");
  return { startDate: toISODate(start.toDate()), endDate: toISODate(end.toDate()) };
}

export default function CalendarDashboard() {
  const [currentDate, setCurrentDate] = useState<Moment>(moment());
  const [view, setView] = useState<CalendarView>("month");

  const [state, setState] = useState<State>({
    tasks: [],
    loading: true,
    formOpen: false,
    selectedTask: null,
  });

  const [docGate, setDocGate] = useState<DocGateState>({
    open: false,
    task: null,
    pendingStatus: null,
  });

  function fetchTasks(date: Moment, currentView: CalendarView) {
    setState((prev) => ({ ...prev, loading: true }));
    const { startDate, endDate } = getViewRange(date, currentView);
    const loggedUserId = localStorage.getItem("useId") ?? undefined;
    const url = buildUrl(urlEnum.tasks, {
      startDate,
      endDate,
      assigneeId: loggedUserId,
      perPage: String(TASKS_MAX_PER_PAGE),
    });
    getData(url)
      .then((res: any) => {
        setState((prev) => ({
          ...prev,
          tasks: res?.data?.tasks ?? res?.data ?? [],
          loading: false,
        }));
      })
      .catch(() => setState((prev) => ({ ...prev, loading: false })));
  }

  useEffect(() => {
    fetchTasks(currentDate, view);
  }, [currentDate, view]);

  function handlePrev() {
    setCurrentDate((prev) => prev.clone().subtract(1, view === "month" ? "month" : "week"));
  }

  function handleNext() {
    setCurrentDate((prev) => prev.clone().add(1, view === "month" ? "month" : "week"));
  }

  function handleToday() {
    setCurrentDate(moment());
  }

  function handleViewChange(newView: CalendarView) {
    setView(newView);
  }

  function handleDayClick(dateStr: string) {
    const template = new TaskModel();
    template.start_date = dateStr;
    setState((prev) => ({ ...prev, formOpen: true, selectedTask: template }));
  }

  function handleTaskClick(task: TaskModel) {
    setState((prev) => ({ ...prev, formOpen: true, selectedTask: task }));
  }

  function closeForm() {
    setState((prev) => ({ ...prev, formOpen: false, selectedTask: null }));
  }

  function handleSave(values: TaskModel) {
    const isEditing = !!values.id;
    const request = isEditing
      ? postData(`${urlEnum.tasks}/${values.id}`, values)
      : postData(urlEnum.tasks, values);
    request
      .then(() => {
        closeForm();
        fetchTasks(currentDate, view);
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch((_) => {});
  }

  function handleDocGateClose() {
    setDocGate({ open: false, task: null, pendingStatus: null });
    fetchTasks(currentDate, view);
  }

  async function handleDocGateConfirm(file: File) {
    const { task, pendingStatus } = docGate;
    if (!task?.id || !pendingStatus) return;
    const formData = objectToFormData({ ...task, status: pendingStatus });
    formData.append("document", file);
    await postData(`${urlEnum.tasks}/${task.id}`, formData);
    setDocGate({ open: false, task: null, pendingStatus: null });
    fetchTasks(currentDate, view);
  }

  return (
    <div className={styles.container}>
      <CalendarHeader
        currentDate={currentDate}
        view={view}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onViewChange={handleViewChange}
      />

      <CalendarLegend />

      {state.loading ? (
        <div className={styles.loadingWrapper}>
          <CircularProgress />
        </div>
      ) : view === "month" ? (
        <CalendarMonthView
          currentDate={currentDate}
          tasks={state.tasks}
          onTaskClick={handleTaskClick}
          onDayClick={handleDayClick}
        />
      ) : (
        <CalendarWeekView
          currentDate={currentDate}
          tasks={state.tasks}
          onTaskClick={handleTaskClick}
          onDayClick={handleDayClick}
        />
      )}

      {state.formOpen && (
        <TaskForm
          name={componentNames.TaskForm}
          open={state.formOpen}
          task={state.selectedTask}
          onClose={closeForm}
          onSave={handleSave}
        />
      )}

      {docGate.open && docGate.task && (
        <TaskDocGateDialog
          open={docGate.open}
          task={docGate.task}
          onClose={handleDocGateClose}
          onConfirm={handleDocGateConfirm}
        />
      )}
    </div>
  );
}
